import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { SelectItemTypes } from '../../CommonTypes'
import { InputGroupPropsTypes } from '../inputComp/InputGroupType'
import { CloseIcon, DepartmentIcon, EarlyWarningIcon, IdIcon, SearchIcon } from '../iconSet/IconSet'
import { sideFieldCount } from './CommonMethods'
import InputGroup from '../inputComp/InputGroup'
import { EarlyWarningTypes } from './CreateWarningTypes'
import { APIAddNewTemplate, APIGetAllTemplates, APIGetNewSequence, APIGetTemplate, APIGetWarningNames, APIRequestNewTemplate, APIRequestUpdateTemplate, APIResponseWarningName, APITemplateResponse, APIUpdateTemplate } from '../../services/APITemplateService'
import AlertDialog from '../popupComp/AlertDialog'
import LoadingScreen from '../loadingScreenComp/LoadingScreen'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { warningMap } from './FixedDataSet'

const SetupTemplate = () => {

    const location = useLocation()

    const navigator = useNavigate()
    
    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK

    const showAlertDialog = (title: string, description: string, yesCallback?: () => void, okCallback?: () => void ) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback);   // Will trigger only on OK
        setAlertDialogState(true);
    }

    const [warningTypes, setWarningTypes] = useState<SelectItemTypes[]>([
        {value: "HRW", name: "heavy rain warning"},
        {value: "HWN", name: "high wind warning"},
        {value: "FLW", name: "flood warning"},
        {value: "LIG", name: "sever lightning warning"},
        {value: "LSW", name: "land slide warning"},
        {value: "MWA", name: "marine weather warning"},
        {value: "TW", name: "tsunami warning"},
        {value: "CUS", name: "custom warning"},
        {value: "GUS", name: "gust warning"},
    ])

    // Primary cause
    const [primaryCause, setPrimaryCause] = useState<SelectItemTypes[]>([
        { name: "Southwest monsoon", value: "southwest_monsoon" },
        { name: "Northeast monsoon", value: "northeast_monsoon" },
        { name: "Low level atmospheric disturbance", value: "low_level_atmospheric_disturbance" },
    ])

    // Lightning alert level
    const [lightningAlertLevel, setLightningAlertLevel] = useState<SelectItemTypes[]>([
        { name: "Low", value: "low" },
        { name: "Moderate", value: "moderate" },
        { name: "High", value: "high" },
    ])

    // Severity level
    const [severityLevel, setSeverityLevel] = useState<SelectItemTypes[]>([
        { name: "White", value: "white" },
        { name: "Yellow", value: "yellow" },
        { name: "Amber", value: "amber" },
        { name: "Red", value: "red" },
    ])

    const [templateData, setTemplateData] = useState<APITemplateResponse>()
    const [warningNames, setWarningName] = useState<APIResponseWarningName[]>([])

    const [filteredWarningName, setFilteredWarningName] = useState<APIResponseWarningName[]>([])
    const [filteredWarningId, setFilteredWarningId] = useState<APITemplateResponse[]>([])

    const [isDataPickerOpen, setIsDataPickerOpen] = useState<boolean>(false);
    const [isIdDataPickerOpen, setIdIsDataPickerOpen] = useState<boolean>(false);

    // Warning name
    const [warningType, setWarningType] = useState<EarlyWarningTypes>()
    
    // Loading screen
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const warningTypeRef = useRef<HTMLSelectElement | null>(null)
    const messageEnRef = useRef<HTMLTextAreaElement | null>(null)
    const messageDvRef = useRef<HTMLTextAreaElement | null>(null)
    const messageSiRef = useRef<HTMLTextAreaElement | null>(null)
    const primaryCauseRef = useRef<HTMLSelectElement | null>(null)
    const lightningRiskLevelRef = useRef<HTMLSelectElement | null>(null)
    const preRequestDocumentRef = useRef<HTMLInputElement | null>(null)
    const templateNameRef = useRef<HTMLInputElement | null>(null)
    const severityRef = useRef<HTMLSelectElement | null>(null)
    const templateDescriptionRef = useRef<HTMLTextAreaElement | null>(null)
    const warningNameRef = useRef<HTMLInputElement | null>(null)
    const warningNameValueRef = useRef<string>('')
    const warningIdRef = useRef<HTMLInputElement | null>(null)

    const handleChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as EarlyWarningTypes
        setWarningType(value)
    }

    const handleChange = async (e: any) => {
        const { name, value } = e.target;
    }

    const dataMap1: InputGroupPropsTypes[] = useMemo(() => {
        let base: InputGroupPropsTypes[] = [
            {
                type: "text",
                icon: <EarlyWarningIcon width="1.5rem" color="var(--color-main-white)" />,
                color: "var(--color-accent-bg)",
                placeholder: "Select Template",
                label: "Template ID",
                name: "template_id",
                componentInputElement: warningIdRef,
                dataPicker: true,
                isDataPickerOpen: isIdDataPickerOpen,
                setIsDataPickerOpen: setIdIsDataPickerOpen,
                required: true,
                disabled: true,
            },
            {
                type: 'select',
                icon: <EarlyWarningIcon width="1.5rem" color="var(--color-main-white)" />,
                color: 'var(--color-accent-bg)',
                placeholder: 'Not selected',
                label: 'Warning type',
                name: 'warning_type',
                required: true,
                componentSelectElement: warningTypeRef,
                optionList: warningTypes,
                onChange: handleChangeValue,
                disabled: false
            },
            {
                type: "text",
                icon: <EarlyWarningIcon width="1.5rem" color="var(--color-main-white)" />,
                color: "var(--color-accent-bg)",
                placeholder: "Select workflow ",
                label: "Workflow",
                name: "workflow",
                componentInputElement: warningNameRef,
                onChange: handleChange,
                dataPicker: warningType ? true : false,
                isDataPickerOpen: isDataPickerOpen,
                setIsDataPickerOpen: setIsDataPickerOpen,
                required: true,
                disabled: true,
            },
            {
                type: 'text',
                icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
                color: 'var(--color-accent-bg)',
                placeholder: 'Template name',
                label: 'Template name',
                name: 'template_name',
                required: true,
                componentInputElement: templateNameRef,
                disabled: warningType ? false : true
            },
            {
                type: 'select',
                icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
                color: 'var(--color-accent-bg)',
                placeholder: 'Not selected',
                label: 'Severity',
                name: 'severity',
                required: true,
                componentSelectElement: severityRef,
                optionList: severityLevel,
                disabled: warningType ? false : true
            },
            {
                type: 'text',
                icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
                color: 'var(--color-accent-bg)',
                placeholder: 'Enter pre-request document',
                label: 'pre-request document',
                name: 'preRequestDocument',
                required: warningType ? false : true,
                componentInputElement: preRequestDocumentRef,
            }
        ]

        if (warningType === "HRW" || warningType === "HWN" || warningType === "LSW" || warningType === "CUS" || warningType === "GUS")
        {
            base.push({
                type: 'select',
                icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
                color: 'var(--color-accent-bg)',
                label: 'primary cause',
                name: 'PrimaryCause',
                required: false,
                componentSelectElement: primaryCauseRef,
                placeholder: 'Not selected',
                optionList: primaryCause,
                disabled: warningType ? false : true
            })
        }

        if (warningType === "MWA" || warningType === "TW")
        {
            base.push({
                type: 'select',
                icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
                color: 'var(--color-accent-bg)',
                name: 'LightningRiskLevel',
                label: 'lightning risk level',
                required: false,
                placeholder: "Not selected",
                componentSelectElement: lightningRiskLevelRef,
                optionList: lightningAlertLevel,
                disabled: warningType ? false : true
            })
        }
        return base
    }, [warningType, filteredWarningName])

    const dataMap2: InputGroupPropsTypes[] = [
        {
            type: 'text-area',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Template description',
            label: 'Template description',
            name: 'template_description',
            required: true,
            componentTextArea: templateDescriptionRef,
            disabled: warningType ? false : true
        },
        {
            type: 'text-area',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'English Message',
            label: 'English message',
            name: 'english_message',
            required: true,
            componentTextArea: messageEnRef,
            disabled: warningType ? false : true
        },
        {
            type: 'text-area',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Dhivehi Message',
            label: 'Dhivehi message',
            name: 'dhivehi_message',
            required: true,
            componentTextArea: messageDvRef,
            disabled: warningType ? false : true
        },
        {
            type: 'text-area',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Bengali Message',
            label: 'Bengali message',
            name: 'bengali_message',
            required: true,
            componentTextArea: messageSiRef,
            disabled: warningType ? false : true
        }
    ]

    // Handle template submit
    const handleSubmit = async () => {
        setIsLoading(true)
        if (!warningTypeRef.current || !messageEnRef.current || !messageDvRef.current || !messageSiRef.current || !templateNameRef.current || !severityRef.current || !templateDescriptionRef.current || !warningNameRef.current || !warningIdRef.current) 
        {
            showAlertDialog("Error", "Please fill all required fields.");
            setIsLoading(false);
            return;
        }

        const primaryCause = primaryCauseRef.current ? primaryCauseRef.current.value : "";
        const lightning = lightningRiskLevelRef.current ? lightningRiskLevelRef.current.value : "";
        const preRequestDocument = preRequestDocumentRef.current ? preRequestDocumentRef.current.value : "";

        const request: APIRequestNewTemplate = {
            id: warningIdRef.current.value,
            type: warningTypeRef.current.value,
            warningName: warningNameValueRef.current,
            name: templateNameRef.current.value,
            description: templateDescriptionRef.current.value,
            severity: severityRef.current.value,
            preRequestDocument,
            primaryCause,
            lightning,
            messageEn: messageEnRef.current.value,
            messageDv: messageDvRef.current.value,
            messageSi: messageSiRef.current.value,
            messageTa: "",
        }

        const response = await APIAddNewTemplate(request)

        if (response.status)
        {
            showAlertDialog("Success", "✅ Warning template submitted successfully!", undefined, () => navigator('/early_warning/generate_warning'));
        }
        else 
        {
            showAlertDialog("Error", `❌ ${response.message}`);
        }
        setIsLoading(false)
    }

    // Handle template submit
    const handleUpdate = async () => {
        setIsLoading(true)
        if (!templateData || !warningTypeRef.current || !messageEnRef.current || !messageDvRef.current || !messageSiRef.current || !templateNameRef.current || !severityRef.current || !templateDescriptionRef.current || !warningNameRef.current) 
        {
            showAlertDialog("Error", "Please fill all required fields.");
            setIsLoading(false);
            return;
        }

        const primaryCause = primaryCauseRef.current ? primaryCauseRef.current.value : "";
        const lightning = lightningRiskLevelRef.current ? lightningRiskLevelRef.current.value : "";
        const preRequestDocument = preRequestDocumentRef.current ? preRequestDocumentRef.current.value : "";

        const request: APIRequestUpdateTemplate = {
            id: templateData.id,
            type: warningTypeRef.current.value,
            warningName: warningNameValueRef.current,
            name: templateNameRef.current.value,
            description: templateDescriptionRef.current.value,
            severity: severityRef.current.value,
            preRequestDocument,
            primaryCause,
            lightning,
            messageEn: messageEnRef.current.value,
            messageDv: messageDvRef.current.value,
            messageSi: messageSiRef.current.value,
            messageTa: "",
        }

        const response = await APIUpdateTemplate(request)

        if (response.status)
        {
            showAlertDialog("Success", "✅ Warning template updated successfully!", undefined, () => navigator('/early_warning/generate_warning'));
        }
        else 
        {
            showAlertDialog("Error", `❌ ${response.message}`);
        }
        setIsLoading(false)
    }

    // Filter warning names by type
    useEffect(() => {
        setFilteredWarningName(prevState => {
            return warningNames.filter(item => item.hazardType === warningType)
        })
    }, [warningType])

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const id = queryParams.get("id");

        const fetchData = async () => {
            setIsLoading(true);

            try 
            {
                // 1. Get warning names first
                const warningNamesResponse = await APIGetWarningNames();
                setWarningName(warningNamesResponse)

                // 2. Then get template details if id exists
                if (id) 
                {
                    const templateResponse = await APIGetTemplate(id);
                    if (templateResponse.data) setTemplateData(templateResponse.data)
                }
            } 
            catch (error) 
            {
                console.error("Error fetching data:", error);
            } 
            finally 
            {
                setIsLoading(false);
            }
        }

        const getAllTemplateData = async () => {
            const response = await APIGetAllTemplates();

            if (response.status){
                setFilteredWarningId(response.data)
            }
        }

        const nextSequence = async () => {
            const response = await APIGetNewSequence()

            if (response.status){
                //console.log("SEQ ->", response.message)
                if (warningIdRef.current){
                    warningIdRef.current.value = response.message
                }
            }
        }

        fetchData();
        getAllTemplateData();

        if(!id){
            nextSequence()
        }
        

    }, [])

    useEffect(() => {
        if (!templateData) return

        if (!warningNameRef.current || !warningTypeRef.current || !messageEnRef.current || !messageDvRef.current || !messageSiRef.current || !templateNameRef.current || !severityRef.current || !templateDescriptionRef.current || !warningIdRef.current) return
        //console.log("TEST ->", templateData)

        setWarningType(templateData.type)
        warningTypeRef.current.value = templateData.type
        messageEnRef.current.value = templateData.messageEn
        messageDvRef.current.value = templateData.messageDv
        messageSiRef.current.value = templateData.messageSi
        templateNameRef.current.value = templateData.name
        severityRef.current.value = templateData.severity
        templateDescriptionRef.current.value = templateData.description
        warningNameRef.current.value = templateData.warningName
        warningNameValueRef.current = templateData.warningName
        warningIdRef.current.value = templateData.id

        const id = warningNames.find(item => item.warningId === templateData.warningName)

        if (!id) return
        warningNameRef.current.value = id.username

        const type = templateData.type

        if (type === "HRW" || type === "HWN" || type === "LSW" || type === "CUS" || type === "GUS")
        {
            if (primaryCauseRef.current){
                primaryCauseRef.current.value = templateData.primaryCause
            }
        }

        if (type === "HRW" || type === "HWN" || type === "FLW" || type === "LIG" || type === "LSW" || type === "MWA" || type === "TW" || type === "CUS" || type === "GUS")
        {
            if (preRequestDocumentRef.current){
                preRequestDocumentRef.current.value = templateData.preRequestDocument
            }
        }

        if (type === "MWA" || type === "TW")
        {
            if (lightningRiskLevelRef.current){
                lightningRiskLevelRef.current.value = templateData.lightning
            }
            
        }
    }, [templateData, warningNames, warningType, filteredWarningName])

    const handleChangeExistingDataSearch = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") 
        {
            setFilteredWarningName(prevState => {
                return warningNames.filter(item => item.hazardType === warningType)
            })
        } 
        else 
        {
            setFilteredWarningName(prevState => {
                return warningNames.filter(item => item.hazardType === warningType && item.username.toLowerCase().includes(event.target.value.toLowerCase()))
            })
        }
    }

    const handleClickFilteredWarningNames = (warning: APIResponseWarningName) => {
        if (!warningNameRef.current) return

        warningNameRef.current.value = warning.username
        warningNameValueRef.current = warning.warningId ?? ""
        setIsDataPickerOpen(false)
    }

    const handleClickFilteredWarningIds = (warning: APITemplateResponse) => {
        setIdIsDataPickerOpen(false)
        const url = `/early_warning/setup_template?id=${warning.id}`
        window.location.href = url
    }

    return (
        <div className={`container-wrapper`}>
            <span className={`title`}>Setup Early Warning Template</span>
            <div className={`create-template`}>
                <div className={`grid-layout-create-template`}>
                    <div className={`left-side`}>
                        {dataMap1.slice(0, sideFieldCount(dataMap1.length)).map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>
                    <div className={`right-side`}>
                        {dataMap1.slice(sideFieldCount(dataMap1.length)).map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>
                    <div className='message'>
                        {dataMap2.map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>
                    <div className={`button-wrapper`} style={{gridArea: 'button'}}>
                        <div className={`left-side`}></div>
                        <div className={`right-side`}>
                            <button className={`button-normal`} onClick={(e) => navigator('/early_warning/generate_warning')}>Back</button>
                            {templateData === undefined ? (
                                <button className={`button-normal`} onClick={(e) => {
                                    showAlertDialog(
                                        "Confirm Submit",
                                        "Are you sure you want to submit this warning template?",
                                        () => handleSubmit(), undefined)
                                }}>save</button>
                            ) : (
                                <button className={`button-normal`} onClick={(e) => {
                                    showAlertDialog(
                                        "Confirm Submit",
                                        "Are you sure you want to update this warning template?",
                                        () => handleUpdate(), undefined)
                                }}>Update</button>
                            )
                        }
                            
                        </div>
                    </div>
                </div>
            </div>
            {alertDialogState && (
                <AlertDialog title={alertTitle} description={alertDescription} windowState={alertDialogState} setWindowState={setAlertDialogState} function={alertYesCallback} okFunction={alertOkCallback} />
            )}
            
            {isLoading && (
                <LoadingScreen />
            )}
            {isDataPickerOpen && (
                <div className="data-picker-table-wrapper">
                    <div className="data-picker-table-container">
                        <div className="search-wrapper">
                            <input type="text" placeholder="Search warning type" onChange={(e) => handleChangeExistingDataSearch(e)}/>
                            <div className="search-icon">
                                <SearchIcon width="1rem" color="var(--color-main-white)" />
                            </div>
                            <div className="close-icon" onClick={(e) => setIsDataPickerOpen(false)} >
                                <CloseIcon width="1.5rem" color="var(--color-text)" />
                            </div>
                        </div>
                        <div className="data-wrapper">
                            {filteredWarningName.map((warning, index) => (
                                <div key={index} className="row" onClick={(e) => handleClickFilteredWarningNames(warning)} >
                                    <span>{warning.username}</span>
                                    <span>{warningMap.get(warning.hazardType)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {isIdDataPickerOpen && (
                <div className="data-picker-table-wrapper">
                    <div className="data-picker-table-container">
                        <div className="search-wrapper">
                            <input type="text" placeholder="Search Template Name" onChange={(e) => handleChangeExistingDataSearch(e)}/>
                            <div className="search-icon">
                                <SearchIcon width="1rem" color="var(--color-main-white)" />
                            </div>
                            <div className="close-icon" onClick={(e) => setIdIsDataPickerOpen(false)} >
                                <CloseIcon width="1.5rem" color="var(--color-text)" />
                            </div>
                        </div>
                        <div className="data-wrapper">
                            {filteredWarningId.map((warning, index) => (
                                <div key={index} className="row" onClick={(e) => handleClickFilteredWarningIds(warning)} >
                                    <span>{warning.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default SetupTemplate